// NotificacionesUsuario.js — Merge JCUMAS 060.013 + SectorBXII
import React, { useEffect, useState } from "react";
import { Spinner, Button, Container } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import FirmaNotificacion from "../FirmaNotificacion";
import SubsanacionNotificacion from "../SubsanacionNotificacion";
import Header from "../Components/Header";

const BASE_URL = process.env.REACT_APP_API_URL;

const NotificacionesUsuario = ({ onActualizarLeidas }) => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [notificacionParaFirmar, setNotificacionParaFirmar] = useState(null);
  const [filtro, setFiltro] = useState("todas");
  const [descargando, setDescargando] = useState(null);
  const [mostrarSubsanacion, setMostrarSubsanacion] = useState(false);
  const [notificacionParaSubsanar, setNotificacionParaSubsanar] = useState(null);

  const usuarioId = localStorage.getItem("id");

  useEffect(() => {
    fetchNotificaciones();
  }, [usuarioId]);

  const fetchNotificaciones = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/notificaciones/usuarios/${usuarioId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (!response.ok) throw new Error("Error al obtener las notificaciones");
      const data = await response.json();

      const dataConSolicitudes = await Promise.all(
        data.map(async (noti) => {
          if (noti.SolicitudID) {
            try {
              const res = await fetch(
                `${BASE_URL}/solicitudes/${noti.SolicitudID}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
              );
              if (res.ok) {
                const solicitud = await res.json();
                return { ...noti, SolicitudDescripcion: solicitud.Descripcion };
              }
            } catch (e) {
              console.error("Error obteniendo solicitud:", e);
            }
          }
          return { ...noti, SolicitudDescripcion: null };
        })
      );

      setNotificaciones(dataConSolicitudes);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const abrirModalFirma = (notificacion) => {
    const fechaActual = new Date();
    const formatDate = (date) => date.toISOString().split("T")[0];
    const formatDateTime = (date) =>
      date.toISOString().replace("T", " ").split(".")[0];

    setNotificacionParaFirmar({
      ...notificacion,
      FechaEnvio: formatDate(new Date(notificacion.FechaEnvio)),
      FechaFirma: formatDate(fechaActual),
      FechaRecepcion: formatDateTime(fechaActual),
    });
    setMostrarModal(true);
  };

  const cerrarModalFirma = () => {
    setMostrarModal(false);
    setNotificacionParaFirmar(null);
    fetchNotificaciones();
    if (typeof onActualizarLeidas === "function") onActualizarLeidas();
  };

  const abrirModalSubsanacion = (notificacion) => {
    setNotificacionParaSubsanar(notificacion);
    setMostrarSubsanacion(true);
  };

  const cerrarModalSubsanacion = () => {
    setMostrarSubsanacion(false);
    setNotificacionParaSubsanar(null);
    fetchNotificaciones();
  };

  // Descarga llamando al endpoint dedicado — sin base64 en memoria
  const handleDownload = async (documentoId) => {
    setDescargando(documentoId);
    try {
      const response = await fetch(
        `${BASE_URL}/documentos/${documentoId}/download`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (!response.ok) throw new Error("Error al descargar");

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "documento.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al descargar:", error);
      alert("No se pudo descargar el documento.");
    } finally {
      setDescargando(null);
    }
  };

  const getTipoInfo = (notificacion) => {
    if (notificacion.Tipo === "subsanacion") {
      return { icon: "bi-exclamation-triangle", color: "warning", label: "Subsanación requerida" };
    }
    if (notificacion.Tipo === "justificante") {
      return { icon: "bi-check-circle", color: "primary", label: "Justificante de registro" };
    }
    return { icon: "bi-bell", color: "primary", label: "Notificación" };
  };

  return (
    <div className="d-flex flex-column">
      <Header />
      <main className="flex-grow-1">
      <Container fluid className="py-3 px-4">
      <h4 className="section-title mt-2 mb-2">
        <i className="bi bi-envelope text-primary"></i> Notificaciones
      </h4>
      <p className="text-muted mb-5">
        En esta sección puede consultar y firmar sus notificaciones recibidas a
        través de la Oficina Virtual.
      </p>

      <div className="mb-3">
        <Button
          variant={filtro === "todas" ? "primary" : "outline-primary"}
          size="sm"
          className="me-2"
          onClick={() => setFiltro("todas")}
        >
          Todas
        </Button>
        <Button
          variant={filtro === "firmadas" ? "primary" : "outline-primary"}
          size="sm"
          className="me-2"
          onClick={() => setFiltro("firmadas")}
        >
          Firmadas
        </Button>
        <Button
          variant={filtro === "pendientes" ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => setFiltro("pendientes")}
        >
          Pendientes
        </Button>
      </div>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
          <p className="mt-3">Cargando notificaciones...</p>
        </div>
      ) : error ? (
        <p className="text-danger text-center">{error}</p>
      ) : notificaciones.length === 0 ? (
        <p className="text-center">No tienes notificaciones.</p>
      ) : (
        <div>
          {notificaciones
            .filter((n) => {
              if (filtro === "firmadas") return n.Firmado;
              if (filtro === "pendientes") return !n.Firmado;
              return true;
            })
            .map((notificacion, idx) => {
              const tipoInfo = getTipoInfo(notificacion);
              const pendiente = !notificacion.Firmado;
              return (
              <div
                key={notificacion.NotificacionID}
                className={
                  (idx === 0 ? "" : "border-top ") +
                  "d-flex py-3" +
                  (pendiente ? " px-3 rounded-3" : "")
                }
                style={pendiente ? { backgroundColor: "var(--sp-primary-soft)" } : undefined}
              >
                <div className="flex-shrink-0 me-3">
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center bg-${tipoInfo.color} bg-opacity-10`}
                    style={{ width: 42, height: 42 }}
                  >
                    <i className={`bi ${tipoInfo.icon} text-${tipoInfo.color}`} style={{ fontSize: "1.15rem" }}></i>
                  </div>
                </div>

                <div className="flex-grow-1">
                  <div className="d-flex align-items-start justify-content-between flex-wrap gap-1">
                    <span className={`fw-bold text-${tipoInfo.color}`}>{tipoInfo.label}</span>
                    <small className="text-muted text-nowrap">
                      {new Date(notificacion.FechaEnvio).toLocaleDateString(
                        "es-ES",
                        { year: "numeric", month: "long", day: "numeric" }
                      )}
                    </small>
                  </div>

                  {notificacion.Tipo === "subsanacion" && (
                    <span className={`badge mt-1 ${notificacion.Subsanada ? "bg-success" : "bg-warning text-dark"}`}>
                      {notificacion.Subsanada ? "Subsanada" : "Requiere subsanación"}
                    </span>
                  )}

                  {notificacion.Asunto && (
                    <div className="fw-bold mt-1 mb-1">{notificacion.Asunto}</div>
                  )}

                  {notificacion.SolicitudDescripcion && (
                    <div className="mb-2">
                      <strong>Solicitud relacionada:</strong>{" "}
                      {notificacion.SolicitudDescripcion}
                    </div>
                  )}

                  <div
                    className="mt-1"
                    style={{ color: notificacion.Firmado ? "inherit" : "red" }}
                  >
                    {notificacion.Firmado ? (
                      <p className="mb-2">{notificacion.Descripcion}</p>
                    ) : (
                      "Esta notificación requiere firma electrónica para visualizarse."
                    )}
                  </div>

                  {/* Botón de descarga — usa DocumentoID en lugar del base64 */}
                  {notificacion.Firmado && notificacion.DocumentoID && (
                    <div className="mb-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={descargando === notificacion.DocumentoID}
                        onClick={() => handleDownload(notificacion.DocumentoID)}
                      >
                        {descargando === notificacion.DocumentoID ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <><i className="bi bi-download"></i> <u>Ver documento</u></>
                        )}
                      </Button>
                    </div>
                  )}

                  <div
                    className="d-flex justify-content-between flex-wrap gap-2 mt-2 pt-2"
                    style={{ borderTop: "1px solid var(--sp-border-strong)" }}
                  >
                    {notificacion.Firmado ? (
                      <span className="text-success fw-bold fs-6 d-flex align-items-center">
                        <i className="bi bi-check-circle-fill me-2"></i>Notificación firmada
                      </span>
                    ) : (
                      <Button
                        variant="outline-primary"
                        onClick={() => abrirModalFirma(notificacion)}
                      >
                        <i className="bi bi-pencil-square me-2"></i> Firmar
                        notificación
                      </Button>
                    )}

                    {notificacion.Tipo === "subsanacion" && notificacion.Firmado && (
                      notificacion.Subsanada ? (
                        <Button variant="success" disabled>
                          <i className="bi bi-check-lg me-2"></i> Documentación aportada
                        </Button>
                      ) : (
                        <Button
                          variant="warning"
                          onClick={() => abrirModalSubsanacion(notificacion)}
                        >
                          <i className="bi bi-file-earmark-arrow-up me-2"></i> Aportar documentación
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </div>
              );
            })}
        </div>
      )}

      {mostrarModal && notificacionParaFirmar && (
        <FirmaNotificacion
          notificacion={notificacionParaFirmar}
          onFirmada={cerrarModalFirma}
        />
      )}

      {mostrarSubsanacion && notificacionParaSubsanar && (
        <SubsanacionNotificacion
          notificacion={notificacionParaSubsanar}
          onSubsanada={cerrarModalSubsanacion}
          onClose={cerrarModalSubsanacion}
        />
      )}
      </Container>
      </main>
    </div>
  );
};

export default NotificacionesUsuario;